import {getCurrencyAmountForSummary} from "../common/adjustment/constants/errorSummaryUtils";
import {convertStringToPercentage} from "../../utils/utils";
import {Button} from "@amzn/awsui-components-react";
import React from "react";
import {downloadAdjustmentFile} from "../../../../common/fileDownloader/getFileFromS3Util";
import {EmptyState} from "../../../phastos/table-config";
import {useMemo} from "react";
import {isNil} from "lodash";

export const DST_COLUMN_IDENTIFIER = {
    Month: 'month',
    LegalEntity: 'legalEntity',
    MarketplaceId: 'marketplaceId',
    SubBusiness: "subBusiness",
    TotalDSTScopeTransaction: 'totalDSTScopeTransaction',
    TotalTransaction: 'totalTransaction',
    NexusRatio: "nexusRatio",
    DSTRate: "dstRate",
    TotalRevenue: "totalRevenue",
    DSTBaseAmount: "dstBaseAmount",
    DSTLiability : "dstLiability",
    TotalRevenueDstReportingCurrency : "totalRevenueAmountDstReportingCurrency",
    TotalDstBaseAmountDstReportingCurrency: "dstBaseAmountDstReportingCurrency",
    TotalDstLiabilityDstReportingCurrency : "dstLiabilityDstReportingCurrency",
    AdjustmentDescription: "adjustmentDescription",
    UpdatedBy: "updatedBy",
    UpdatedOn: "updatedOn",
    Category : "category",
    DocumentLink: 'link',
};

export const DST_BULK_ADJUSTMENT_FILE_IDENTIFIER = {
    Month: 'Month',
    Category: 'Category',
    LegalEntity: 'LegalEntity',
    MarketPlace: 'MarketPlace',
    SubBusiness: 'Sub Business',
    LocalCurrency: 'LocalCurrency',
    DSTReportingCurrency: "DST Reporting Currency",
    TotalTransactions: "Total Transactions",
    TotalDSTScopeTransaction: "Total DST Scope Transaction",
    TotalRevenueLocalCurrency: "Total Revenue(Local Currency)",
    TotalRevenueDSTReportingCurrency : "Total Revenue(DST Reporting Currency)",
    DSTLiability: "DST Liability(Local Currency)",
    DSTLiabilityDSTReportingCurrency: "DST Liability(DST Reporting Currency)",
    Comment: "Comment",
};

export const API_IDENTIFIER = {
    Month: 'month',
    BusinessCategory: 'businessCategory',
    LegalEntity: 'legalEntity',
    MarketplaceId: 'marketplaceId',
    SubBusiness: 'subBusiness',
    LocalCurrency: 'localCurrency',
    DSTReportingCurrency: "dstReportingCurrency",
    TotalTransactions: "totalTransaction",
    TotalDSTScopeTransaction: "totalDSTScopeTransaction",
    TotalRevenueLocalCurrency: "totalRevenue",
    TotalRevenueDSTReportingCurrency : "totalRevenueAmountDstReportingCurrency",
    DSTLiability: "dstLiability",
    DSTLiabilityDSTReportingCurrency: "dstLiabilityDstReportingCurrency"
};


/**
 * @constant {Array<Object>} dstExcelToAPIFieldsForComparison
 * @description Mapping of Excel column names to API field names for data validation
 */
export const dstExcelToAPIFieldsForComparison = [
    {
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.Month,
        apiKey: API_IDENTIFIER.Month
    },{
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.Category,
        apiKey: API_IDENTIFIER.BusinessCategory
    },
    {
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.LegalEntity,
        apiKey: API_IDENTIFIER.LegalEntity
    },
    {
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.MarketPlace,
        apiKey: API_IDENTIFIER.MarketplaceId
    },
    {
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.SubBusiness,
        apiKey: API_IDENTIFIER.SubBusiness
    },
    {
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.LocalCurrency,
        apiKey: API_IDENTIFIER.LocalCurrency
    },
    {
        excelKey: DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.DSTReportingCurrency,
        apiKey: API_IDENTIFIER.DSTReportingCurrency
    }
];

/**
 * @constant {string[]} dstFieldsToCheckForNumericValue
 * @description Array of field names that require numeric value validation.
 */
export const dstFieldsToCheckForNumericValue = [
    DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.TotalTransactions,
    DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.TotalDSTScopeTransaction,
    DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.TotalRevenueLocalCurrency,
    DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.TotalRevenueDSTReportingCurrency,
    DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.DSTLiability,
    DST_BULK_ADJUSTMENT_FILE_IDENTIFIER.DSTLiabilityDSTReportingCurrency,
];

export const dstAuditTableColumnDefinitions = (dstDispatcher) => [
    {
        id: DST_COLUMN_IDENTIFIER.LegalEntity,
        header: "Legal Entity",
        cell: e => e.additionalDataMap?.legalEntity,
        width: 170,
        minWidth: 200,
        isRowHeader: true,
        sortingField: DST_COLUMN_IDENTIFIER.LegalEntity
    },
    {
        id: DST_COLUMN_IDENTIFIER.MarketplaceId,
        header: "Marketplace Id",
        cell: e => e.additionalDataMap?.marketplaceId,
        width: 170,
        minWidth: 165,
        sortingField: DST_COLUMN_IDENTIFIER.MarketplaceId
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalDSTScopeTransaction,
        header: "Total Transaction DST Scope",
        cell: e => e.additionalDataMap?.totalDSTScopeTransaction,
        width: 240,
        minWidth: 240
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalTransaction,
        header: "Total Transaction",
        cell: e => e.additionalDataMap?.totalTransaction,
        width: 200,
        minWidth: 170
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalRevenue,
        header: "Total Revenue",
        cell: e => e.additionalDataMap?.totalRevenue,
        width: 200,
        minWidth: 170
    },
    {
        id: DST_COLUMN_IDENTIFIER.AdjustmentDescription,
        header: "Adjustment Description",
        cell: e => e.adjustment?.description,
        width: 250,
        minWidth: 250
    },
    {
        id: DST_COLUMN_IDENTIFIER.UpdatedBy,
        header: "Updated By",
        cell: e => e.updatedBy,
        width: 200,
        minWidth: 170
    },
    {
        id: DST_COLUMN_IDENTIFIER.UpdatedOn,
        header: "Updated On",
        cell: e => e.timestamp,
        width: 200,
        minWidth: 170
    },
    {
        id: DST_COLUMN_IDENTIFIER.DocumentLink,
        header: "Document Link",
        cell: item => {
            const docHubDocumentId = item.additionalDataMap?.docHubDocumentId;
            if (docHubDocumentId) {
                return (
                    <Button iconName="download"
                            wrapText={false}
                            onClick={() => downloadAdjustmentFile(dstDispatcher, {
                                docHubDocumentId: docHubDocumentId
                            })}>
                        View Document
                    </Button>
                )
            }
            return (<></>);
        },
        width: 200,
        minWidth: 170
    },
];

export const dstColumnDefinitions = [
    {
        id: DST_COLUMN_IDENTIFIER.Month,
        cell: item => {
            return item.month;
        },
        header: 'Month',
    },{
        id: DST_COLUMN_IDENTIFIER.Category,
        cell: item => {
            return item.businessCategory;
        },
        header: 'Category',
    },
    {
        id: DST_COLUMN_IDENTIFIER.LegalEntity,
        cell: item => {
            return item.legalEntity;
        },
        header: 'Legal Entity',
    },
    {
        id: DST_COLUMN_IDENTIFIER.MarketplaceId,
        cell: item => {
            return item.marketplaceId;
        },
        header: 'Marketplace Id',
    },
    {
        id: DST_COLUMN_IDENTIFIER.SubBusiness,
        cell: item => {
            return item.subBusiness;
        },
        header: 'Sub Business',
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalDSTScopeTransaction,
        cell: item => {
            return getCurrencyAmountForSummary(item.totalDSTScopeTransaction);
        },
        header: 'Total Transaction DST Scope',
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalTransaction,
        cell: item => {
            return getCurrencyAmountForSummary(item.totalTransaction);
        },
        header: 'Total Transaction',
    },
    {
        id: DST_COLUMN_IDENTIFIER.NexusRatio,
        cell: item => {
            return convertStringToPercentage(item.nexusRatio);
        },
        header: 'Nexus Ratio',
    },
    {
        id: DST_COLUMN_IDENTIFIER.DSTRate,
        cell: item => {
            return convertStringToPercentage(item.dstRate);
        },
        header: 'DST Rate',
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalRevenue,
        cell: item => {
            return getCurrencyAmountForSummary(item.totalRevenue, item.localCurrency);
        },
        header: 'Total Revenue (Local Currency)',
    },
    {
        id: DST_COLUMN_IDENTIFIER.DSTBaseAmount,
        cell: item => {
            return getCurrencyAmountForSummary(item.dstBaseAmount, item.localCurrency);
        },
        header: 'DST Base Amount (Local Currency)',
    },
    {
        id: DST_COLUMN_IDENTIFIER.DSTLiability,
        cell: item => {
            return getCurrencyAmountForSummary(item.dstLiability, item.localCurrency);
        },
        header: 'DST Liability (Local Currency)',
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalRevenueDstReportingCurrency,
        cell: item => {
            return getCurrencyAmountForSummary(item.totalRevenueAmountDstReportingCurrency, item.dstReportingCurrency);
        },
        header: 'Total Revenue (DST Reporting Currency)',
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalDstBaseAmountDstReportingCurrency,
        cell: item => {
            return getCurrencyAmountForSummary(item.dstBaseAmountDstReportingCurrency, item.dstReportingCurrency);
        },
        header: 'DST Base Amount (DST Reporting Currency)',
    },
    {
        id: DST_COLUMN_IDENTIFIER.TotalDstLiabilityDstReportingCurrency,
        cell: item => {
            return getCurrencyAmountForSummary(item.dstLiabilityDstReportingCurrency, item.dstReportingCurrency);
        },
        header: 'DST Liability (DST Reporting Currency)',
    },
];

export const INVALID_INPUT = "Invalid Input";

export const DST_ACCOUNTING_COLUMN_ID = Object.freeze({
    MONTH: "month",
    CATEGORY: "businessCategory",
    LEGAL_ENTITY: "legalEntity",
    COMPANY_CODE: "companyCode",
    CHANNEL: "inputChannel",
    SUB_BUSINESS: "subBusiness",
    DEBIT_OR_CREDIT: "debitOrCredit",
    ACCOUNT_NUMBER: "inputAccountNumber",
    COST_CENTER: "inputCostCenter",
    LOCATION: "inputLocation",
    PRODUCT_LINE: "inputProductLine",
    PROJECT: "inputProject",
    SET_OF_BOOKS: "setOfBooks",
    GL_LINE_DESCRIPTION: "glLineDescription",
    FUNCTIONAL_AMOUNT: "functionalAmount",
    FUNCTIONAL_AMOUNT_CURRENCY: "functionalAmountCurrency",
    DST_LIABILITY_REPORTING_CURRENCY: "dstBaseAmountDstReportingCurrency",
    DST_REPORTING_CURRENCY: "dstReportingCurrency",
});

export const ACCOUNTING_TAB_FILTERING_PROPERTIES = Object.freeze([
    {
        propertyLabel: "Legal Entity",
        key: "legalEntity",
        groupValuesLabel: "Legal Entity",
        operators: ["=", "!="],
    },
    {
        propertyLabel: "Business Category",
        key: "businessCategory",
        groupValuesLabel: "Business Category",
        operators: ["=", "!="],
    },
    {
        propertyLabel: "Sub Business",
        key: "subBusiness",
        groupValuesLabel: "Sub Business",
        operators: ["=", "!="],
    },
    {
        propertyLabel: "Debit or Credit",
        key: "debitOrCredit",
        groupValuesLabel: "Debit or Credit",
        operators: ["=", "!="],
    },
    {
        propertyLabel: "Company Code",
        key: "companyCode",
        groupValuesLabel: "Company Code",
        operators: ["=", "!="],
    },
    {
        propertyLabel: "Month",
        key: "month",
        groupValuesLabel: "Month",
        operators: ["=", "!="],
    },
]);

export const INITIAL_PAGE_NUMBER = 1;

export const INITIATE_ADJUSTMENT = "INITIATE_ADJUSTMENT";

export const TAX_ADJUSTMENT_JOB_TYPE  = "TAX_ADJUSTMENT";

export const TableEmptyState = ({type}) => {
    const states = {
        noSummary: {title: "No summary", subtitle: "No summary to display."},
        noMatches: {title: "No matches", subtitle: "We can't find a match."},
    };

    const {title, subtitle} = states[type] || states.noSummary;
    return <EmptyState title={title} subtitle={subtitle}/>;
};

/**
 * Checks whether a transaction should be treated as an original transaction.
 *
 * A transaction is considered valid if:
 * - Its transaction type is "original"
 *
 * This is used on the Entity Breakdown page to display only
 * original
 *
 * @param {Object} transaction - Transaction object
 * @returns {boolean} True if the transaction is original
 */
export function isOriginalTransaction(transaction) {
    if (isNil(transaction.transactionType)) {
        return true;
    }
    return transaction.transactionType === 'original';
};


/**
 * Custom hook for DST Accounting tab column definitions
 */
export const useDSTAccountingColumnDefinition = () => {
    return useMemo(
        () => [
            {
                id: DST_ACCOUNTING_COLUMN_ID.MONTH,
                cell: (item) => item.month,
                header: "Month",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.CATEGORY,
                cell: (item) => item.businessCategory,
                header: "Category",
                sortingField: "businessCategory",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.SUB_BUSINESS,
                cell: (item) => item.subBusiness,
                header: "Sub Business",
                sortingField: "subBusiness",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.LEGAL_ENTITY,
                cell: (item) => item.legalEntity,
                header: "Legal Entity",
                sortingField: "legalEntity",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.COMPANY_CODE,
                cell: (item) => item.companyCode,
                header: "Company Code",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.CHANNEL,
                cell: (item) => item.inputChannel,
                header: "Channel Code",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.DEBIT_OR_CREDIT,
                cell: (item) => item.debitOrCredit,
                header: "Debit or Credit Entry",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.ACCOUNT_NUMBER,
                cell: (item) => item.inputAccountNumber,
                header: "Account Number",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.COST_CENTER,
                cell: (item) => item.inputCostCenter,
                header: "Cost Centre",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.LOCATION,
                cell: (item) => item.inputLocation,
                header: "Location",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.PRODUCT_LINE,
                cell: (item) => item.inputProductLine,
                header: "Product Line",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.PROJECT,
                cell: (item) => item.inputProject,
                header: "Future",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.SET_OF_BOOKS,
                cell: (item) => item.setOfBooks,
                header: "Ledger Id",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.FUNCTIONAL_AMOUNT,
                cell: (item) =>
                    getCurrencyAmountForSummary(
                        item.dstLiabilityDstReportingCurrency,
                        item.dstReportingCurrency
                    ),
                header: "Functional Amount",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.FUNCTIONAL_AMOUNT_CURRENCY,
                cell: (item) => item.dstReportingCurrency,
                header: "Functional Amount Currency",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.DST_LIABILITY_REPORTING_CURRENCY,
                cell: (item) =>
                    getCurrencyAmountForSummary(
                        item.dstLiabilityDstReportingCurrency,
                        item.dstReportingCurrency
                    ),
                header: "DST Liability (Reporting Currency)",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.DST_REPORTING_CURRENCY,
                cell: (item) => item.dstReportingCurrency,
                header: "DST Reporting Currency",
            },
            {
                id: DST_ACCOUNTING_COLUMN_ID.GL_LINE_DESCRIPTION,
                cell: (item) => item.glLineDescription,
                header: "GL Line Description",
            },
        ],
        []
    );
};